import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalService } from 'ngx-bootstrap/modal'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  take,
  withLatestFrom
} from 'rxjs/operators'
import { NotificationService } from 'src/app/core/notification.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { environment } from 'src/environments/environment'
import { AppState } from '../../../core/core.state'
import {
  getBreadCrumb,
  selectLasPageVisited
} from '../../../shared/components/breadcrumb/breadcrumb.selectors'
import {
  BookingFilterValue,
  BulkAction,
  BulkActionEvent
} from '../../../shared/interfaces'
import { ConfigurationService } from '../../../shared/services/configuration.service'
import { productSelectors } from '../../product/product.selectors'
import { Tag } from '../../tag/tag'
import { tagActions } from '../../tag/tag.actions'
import { tagSelectors } from '../../tag/tag.selectors'
import { Booking } from '../booking'
import { bookingActions } from '../booking.actions'
import { bookingSelectors } from '../booking.selectors'
import { BreadCrumbActions } from './../../../shared/components/breadcrumb/breadcrumb.actions'
import { Product } from './../../product/product'
import { productActions } from './../../product/product.actions'
import {
  UrlMatcherName,
  WCAG_TITLE_TRANSLATIONS
} from 'src/app/shared/constants'
import { createUrlMatcher } from 'src/app/shared/until'
import { DeleteBookingsModalComponent } from '../delete-bookings-modal/delete-bookings-modal.component'

type bookingIndexTab =
  | 'calendar'
  | 'list'
  | 'schedule'
  | 'unattended'
  | 'serieProposals'

@UntilDestroy()
@Component({
  selector: 'bookings-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class BookingIndexComponent implements OnInit, AfterViewChecked {
  public isLoading$: Observable<boolean>
  public bookings$: Subject<Booking[]> = new Subject<Booking[]>()
  public allProducts$: Observable<Product[]>
  public bookableProducts$: Observable<Product[]>
  public selectedType: bookingIndexTab = 'calendar'
  public selectedListType: string = 'all'
  public form: UntypedFormGroup
  public accessRights = AccessRights
  public tags$: Observable<Tag[]>
  public BulkAction = BulkAction
  private bookingsFetched: boolean = false
  public total: number = 0
  public page: number = 1
  public limit: number = 25
  public pageCount: number = 0
  public removeSearchTerm$: Subject<boolean> = new Subject<boolean>()
  public searchTerm$ = new Subject<string>()
  public searchTerm
  public bookingModalIsOpen: boolean = false
  public environment = environment
  private fecthBookingDo: number = 0
  public bookingId: number

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private actions$: Actions,
    private cdref: ChangeDetectorRef,
    private configurationService: ConfigurationService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.BulkAction = BulkAction
    this.form = this.fb.group({
      selectedListType: ['all', []],
      dateRange: [
        [
          moment().startOf('day').toDate(),
          moment().startOf('day').add(30, 'days').toDate()
        ],
        []
      ],
      selectedTagIds: [[]]
    })
    this.isLoading$ = this.store.select(bookingSelectors.selectIsLoading)
    this.allProducts$ = this.store.select(productSelectors.selectAll)
    this.store.dispatch(tagActions.fetchMany({}))
    this.bookableProducts$ = this.allProducts$.pipe(
      map((product) => {
        return product.filter((product) => product.bookable)
      })
    )
    this.searchTerm$
      .pipe(distinctUntilChanged(), debounceTime(5), untilDestroyed(this))
      .subscribe((term) => {
        if (this.selectedType == 'list') {
          this.searchTerm = term
          this.page = 1

          this.dispatchFetchBookings({
            startDate: moment(this.form.controls.dateRange.value[0]),
            endDate: moment(this.form.controls.dateRange.value[1])
          })
        }
      })
    this.actions$
      .pipe(
        untilDestroyed(this),
        ofType(bookingActions.upsertMany),
        catchError((error) => of(null))
      )
      .subscribe((action: any) => {
        if (!action) {
          this.bookings$.next([])
          return
        }

        if (this.selectedType == 'calendar' && this.bookingModalIsOpen) {
          return
        }
        if (this.selectedType == 'list') {
          this.total = action?.total
          this.pageCount = action?.pageCount
          this.bookings$.next(action?.entities)
        }
      })
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((form) => {
      if (this.selectedType == 'list') {
        this.removeSearchTerm$.next(true)
        this.searchTerm = null
        this.page = 1

        this.dispatchFetchBookings({
          startDate: moment(this.form.controls.dateRange.value[0]),
          endDate: moment(this.form.controls.dateRange.value[1])
        })
      }
    })

    this.tags$ = combineLatest([
      this.store.select(tagSelectors.selectAll),
      this.configurationService.selectedLang$
    ]).pipe(
      map(([tags, lang]) => {
        return tags.map((tag) => ({
          ...tag,
          description: tag[`description_${lang}`]
        }))
      })
    )

    this.store
      .select(getBreadCrumb)
      .pipe(take(1), withLatestFrom(this.store.select(selectLasPageVisited)))
      .subscribe(([breadCrumb, selectLasPageVisited]) => {
        const matchSerieProposal = createUrlMatcher(
          UrlMatcherName.SERIE_PROPOSAL,
          { id: 'number' }
        )
        if (
          selectLasPageVisited &&
          matchSerieProposal(selectLasPageVisited?.url)
        ) {
          this.selectedType = 'serieProposals'
          this.store.dispatch(
            BreadCrumbActions.setLastPageVisited({
              pageName: 'bookingModule.bookings',
              url: '/bookings',
              titleName: [
                WCAG_TITLE_TRANSLATIONS['bookings']['main'],
                WCAG_TITLE_TRANSLATIONS['bookings'][this.selectedType]
              ]
            })
          )
        } else {
          const matchBooking = createUrlMatcher(UrlMatcherName.BOOKINGS, {
            id: 'number'
          })
          this.selectedType = matchBooking(selectLasPageVisited?.url)
            ? 'list'
            : 'calendar'
        }

        if (this.selectedType == 'list')
          this.dispatchFetchBookings({
            startDate: moment(this.form.controls.dateRange.value[0]),
            endDate: moment(this.form.controls.dateRange.value[1])
          })
      })
    this.store.dispatch(
      BreadCrumbActions.initHistory({
        pageName: 'bookingModule.bookings',
        url: '/bookings',
        titleName: [
          WCAG_TITLE_TRANSLATIONS['bookings']['main'],
          WCAG_TITLE_TRANSLATIONS['bookings'][this.selectedType]
        ]
      })
    )
    if (this.selectedType == 'list' && this.fecthBookingDo == 0) {
      this.dispatchFetchBookings({
        startDate: moment(this.form.controls.dateRange.value[0]),
        endDate: moment(this.form.controls.dateRange.value[1])
      })
    }
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['type']) {
        this.selectedType = params['type']
      }
      if (params['bookingId']) {
        this.bookingId = params['bookingId']
      }
    })
  }
  public setBookingModalIsOpen(bookingModalIsOpen) {
    this.bookingModalIsOpen = bookingModalIsOpen
  }

  ngAfterViewChecked() {
    this.cdref.detectChanges()
  }

  public onClickRow(booking: Booking) {
    this.router.navigate(['/bookings', booking.id])
  }

  public onClickSelectedType(type: bookingIndexTab) {
    if (type == 'list') {
      this.dispatchFetchBookings({
        startDate: moment(this.form.controls.dateRange.value[0]),
        endDate: moment(this.form.controls.dateRange.value[1])
      })
    }

    this.bookingId = null
    this.selectedType = type
    this.store.dispatch(
      BreadCrumbActions.initHistory({
        pageName: 'bookingModule.bookings',
        url: '/bookings',
        titleName: [
          WCAG_TITLE_TRANSLATIONS['bookings']['main'],
          WCAG_TITLE_TRANSLATIONS['bookings'][this.selectedType]
        ]
      })
    )
  }

  public onClickSelectedListType(type: string) {
    this.selectedListType = type
    this.form.controls.selectedListType.patchValue(type)
  }

  public onClickCreate() {
    this.router.navigate(['/bookings/create'])
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.action == BulkAction.DELETE) {
      const modalRef = this.modalService.show(DeleteBookingsModalComponent, {
        initialState: {
          bookingIds: bulkActionEvent.items.map((item) => parseInt(item.id))
        }
      })
      this.modalService.onHide
        .pipe(untilDestroyed(this), take(1))
        .subscribe((event) => {
          this.dispatchFetchBookings({
            startDate: moment(this.form.controls.dateRange.value[0]),
            endDate: moment(this.form.controls.dateRange.value[1])
          })
        })

      return
    }
  }
  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }

  private dispatchFetchBookings({
    startDate = moment().startOf('day'),
    endDate = moment().add(30, 'day')
  }: Partial<BookingFilterValue>) {
    this.fecthBookingDo++
    this.bookings$.next([])
    if (this.searchTerm) {
      if (this.form.controls.selectedListType.value == 'all') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .clone()
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                },
                $or: [
                  {
                    'product.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  },
                  {
                    'linkedProduct.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  }
                ]
              },

              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'member' },
                { field: 'product' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
      } else if (this.form.controls.selectedListType.value == 'unconfirmed') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                },
                'participations.isUnconfirmed': true,
                $or: [
                  {
                    'product.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  },
                  {
                    'linkedProduct.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  }
                ]
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'participations' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
      } else if (this.form.controls.selectedListType.value == 'unattended') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                },
                'participations.attended': false,
                $or: [
                  {
                    'product.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  },
                  {
                    'linkedProduct.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  }
                ]
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'participations' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
      } else if (this.form.controls.selectedListType.value == 'history') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: moment().toISOString()
                },
                $or: [
                  {
                    'product.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  },
                  {
                    'linkedProduct.description': {
                      [CondOperator.CONTAINS_LOW]: this.searchTerm
                    }
                  }
                ]
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
      }
    } else {
      if (this.form.controls.selectedListType.value == 'all') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                }
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
        this.bookingsFetched = true
      } else if (this.form.controls.selectedListType.value == 'unconfirmed') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                },
                'participations.isUnconfirmed': true
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'participations' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
        this.bookingsFetched = true
      } else if (this.form.controls.selectedListType.value == 'unattended') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                startDate: {
                  [CondOperator.GREATER_THAN_EQUALS]: startDate
                    .startOf('day')
                    .toISOString()
                },
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: endDate
                    .clone()
                    .add(1, 'day')
                    .subtract(1, 'second')
                    .toISOString()
                },
                'participations.attended': false
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'participations' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
        this.bookingsFetched = true
      } else if (this.form.controls.selectedListType.value == 'history') {
        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              page: this.page,
              limit: this.limit,
              search: {
                endDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: moment().toISOString()
                }
              },
              sort: { field: 'startDate', order: 'DESC' },
              join: [
                { field: 'tags' },
                { field: 'product' },
                { field: 'member' },
                { field: 'product.translations' },
                { field: 'product.location' },
                { field: 'linkedProduct' },
                //{ field: 'linkedProduct.translations' },
                { field: 'linkedProduct.location' }
              ]
            })
          })
        )
        this.bookingsFetched = true
      }
    }
  }
  public selectNewPage(page) {
    if (this.selectedType == 'list') {
      this.page = page
      this.dispatchFetchBookings({
        startDate: moment(this.form.controls.dateRange.value[0]),
        endDate: moment(this.form.controls.dateRange.value[1])
      })
    }
  }
  public selectNewLimit(limit) {
    if (this.selectedType == 'list') {
      {
        this.limit = limit
        this.page = 1
        this.dispatchFetchBookings({
          startDate: moment(this.form.controls.dateRange.value[0]),
          endDate: moment(this.form.controls.dateRange.value[1])
        })
      }
    }
  }
  public onSearchTerm(searchTerm) {
    if (this.selectedType == 'list') {
      if (searchTerm.length > 0) {
        this.searchTerm$.next(searchTerm)
      } else {
        this.searchTerm = null
        this.page = 1
        this.dispatchFetchBookings({
          startDate: moment(this.form.controls.dateRange.value[0]),
          endDate: moment(this.form.controls.dateRange.value[1])
        })
      }
    }
  }
}
